'use client';

import Image from '@/components/image';
import PromotionLink from '@/components/promotion-link';
import { getImageByLocale, handleLinkByTypeV2 } from '@/utils/config';
import { event_banner_click } from '@/utils/gtm';
import {
  buildPromotion,
  getPromotionName,
  PROMOTION_SECTIONS,
} from '@/utils/gtm/promotions';
import { setCookie } from 'cookies-next';
import { useLocale } from 'next-intl';
import { useCallback, useMemo, useState } from 'react';
import { Autoplay, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import ArrowIcon from '../icons/arrow-icon';

function HeroSlider({
  data: {
    main_slider: { value: mainSlider = [] } = {},
    side_banners: { value: sideBanners = [] } = {},
  } = {},
  category_sliders,
  featured_banners,
  isMobile,
  fromPLP,
}) {
  const locale = useLocale();
  const [swiper, set_swiper] = useState(null);
  const active = isMobile ? 'mobile-active' : 'active';
  const position = isMobile ? 'mobile-position' : 'position';

  const [activeSlide, setActiveSlide] = useState(false);
  const [isLastProduct, setIsLastProduct] = useState(false);

  const sortByPosition = useCallback(
    (a, b) => +a[position] - +b[position],
    [position]
  );

  const activeMainSlider = useMemo(() => {
    return !fromPLP
      ? mainSlider?.filter((c) => c?.[active])?.sort(sortByPosition)
      : category_sliders;
  }, [fromPLP, category_sliders, mainSlider, active, sortByPosition]);
  const activeSideBanners = useMemo(() => {
    return !fromPLP
      ? sideBanners?.filter((c) => c?.[active])?.sort(sortByPosition)
      : featured_banners;
  }, [fromPLP, featured_banners, sideBanners, active, sortByPosition]);

  return (
    <div className='mt-1 grid grid-cols-4 gap-2.5 sm:gap-3 md:mt-1.5 md:gap-3.25 lg:mt-[5px]'>
      {activeMainSlider?.length > 0 && (
        <div
          className={`home-swiper relative col-span-full max-w-[1110px] ${activeSideBanners?.length > 0 ? 'md:col-span-3' : ''}`}
        >
          {activeSlide ? (
            <button
              onClick={() => swiper?.slidePrev()}
              aria-label={locale === 'en' ? 'Previous' : 'السابق'}
              className='absolute start-2.5 top-1/2 z-20 flex size-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/20 hover:bg-black/40 max-sm:hidden'
            >
              <ArrowIcon
                className={`text-white ${locale === 'en' ? 'rotate-90' : '-rotate-90'}`}
              />
            </button>
          ) : null}
          <Swiper
            modules={[Pagination, Autoplay]}
            autoplay={{
              delay: 2500,
              disableOnInteraction: true,
              pauseOnMouseEnter: true,
            }}
            pagination={{ enabled: true, clickable: true }}
            onInit={(e) => set_swiper(e)}
            loop
            onSlideChange={(info) => {
              setIsLastProduct(info?.isEnd);
              info?.activeIndex === 0
                ? setActiveSlide(false)
                : setActiveSlide(true);
            }}
          >
            {activeMainSlider?.map((info, index) => (
              <SwiperSlide key={info?.id || index}>
                <PromotionLink
                  href={handleLinkByTypeV2(info?.url, info?.type)}
                  promotion={buildPromotion({
                    section: fromPLP
                      ? PROMOTION_SECTIONS.plpSlider
                      : PROMOTION_SECTIONS.heroSlider,
                    position: index + 1,
                    id: info?.id,
                    name: getPromotionName(info, ['alt', 'title', 'name']),
                    image: getImageSrc(info, locale, isMobile, fromPLP),
                  })}
                  onClick={() => {
                    event_banner_click({
                      banner_id: info?.id || index + 1,
                      banner_name: info?.[`alt-${locale}`],
                      banner_category: info?.url,
                      section: 'hero-slider',
                    });
                    setCookie(
                      'brand_view_type',
                      fromPLP ? 'category' : 'home-page'
                    );
                  }}
                  target={info?.['open-in-new-tab'] ? '_blank' : '_self'}
                >
                  <Image
                    src={getImageSrc(info, locale, isMobile, fromPLP)}
                    className='h-full w-full rounded-lg object-cover'
                    alt={info?.[`alt-${locale}`]}
                    priority={true}
                    loading='eager'
                    width={isMobile ? 342 : 1110}
                    height={isMobile ? 185 : 600}
                  />
                </PromotionLink>
              </SwiperSlide>
            ))}
          </Swiper>
          {!isLastProduct ? (
            <button
              onClick={() => swiper?.slideNext()}
              aria-label={locale === 'en' ? 'Next' : 'التالي'}
              className='absolute end-2.5 top-1/2 z-20 flex size-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/20 hover:bg-black/40 max-sm:hidden'
            >
              <ArrowIcon
                className={`text-white ${locale === 'en' ? '-rotate-90' : 'rotate-90'}`}
              />
            </button>
          ) : null}
        </div>
      )}
      {activeSideBanners?.length > 0 && (
        <div className='col-span-full gap-4.25 max-md:grid max-md:grid-cols-2 max-md:gap-2.5 md:col-span-1 md:flex md:flex-col'>
          {activeSideBanners?.map((info, index) => (
            <PromotionLink
              key={info?.id || index}
              target={info?.['open-in-new-tab'] ? '_blank' : '_self'}
              promotion={buildPromotion({
                section: fromPLP
                  ? PROMOTION_SECTIONS.plpBanner
                  : PROMOTION_SECTIONS.sideBanners,
                position: index + 1,
                id: info?.id,
                name: getPromotionName(info, ['alt', 'title', 'name']),
                image: getImageSrc(info, locale, isMobile, fromPLP),
              })}
              onClick={() => {
                event_banner_click({
                  banner_id: info?.id || index + 1,
                  banner_name: info?.[`alt-${locale}`],
                  banner_category: info?.url,
                  section: 'hero-slider',
                });
                setCookie(
                  'brand_view_type',
                  fromPLP ? 'category' : 'home-page'
                );
              }}
              href={handleLinkByTypeV2(info?.url, info?.type)}
              className='md:max-h-[140px] xl:max-h-[245px] 2xl:max-h-[292px]'
            >
              <Image
                src={getImageSrc(info, locale, isMobile, fromPLP)}
                className='h-full w-full rounded-lg'
                alt={info?.[`alt-${locale}`]}
                priority={true}
                loading='eager'
                width={isMobile ? 166 : 285}
                height={isMobile ? 170 : 292}
              />
            </PromotionLink>
          ))}
        </div>
      )}
    </div>
  );
}

export default HeroSlider;

const getImageSrc = (info, locale, isMobile, fromPLP) => {
  if (!fromPLP) return getImageByLocale(info, locale, isMobile);
  return `${process.env.NEXT_PUBLIC_IMG_BASEURL}/media/${info?.banner_image}`;
};
